import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import Logo from '../assets/logo.png';
import useErrors from '../hooks/useErrors';
import server from '../services/server';

export default function RecuperarSenha() {
    const { setErrors, enqueueSnackbar } = useErrors();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const profile = params.get('profile') || 'admin';
    const [step, setStep] = useState('request');

    const loginReturnTo =
        profile === 'club' ? '/clube/login' : profile === 'advertiser' ? '/anunciante/login' : '/';

    const getFieldLabel = () => {
        if (profile === 'advertiser' || profile === 'club') return 'E-mail';
        return 'Usuário';
    };

    const getStepTitle = () => (step === 'request' ? 'Recuperar senha' : 'Validar código');

    const getStepDescription = () => {
        if (step === 'request') {
            if (profile === 'club' || profile === 'advertiser') {
                return 'Informe o e-mail de acesso para receber o código.';
            }
            return 'Informe seus dados para receber um código de recuperação no e-mail cadastrado.';
        }
        return 'Informe o código recebido por e-mail e defina uma nova senha.';
    };

    const { values, handleSubmit, handleChange, isSubmitting, setFieldValue } = useFormik({
        initialValues: {
            username: '',
            code: '',
            password: '',
            passwordConfirm: '',
        },
        validationSchema: Yup.object({
            username: Yup.string().trim().required('Campo obrigatório'),
            code: step === 'reset' ? Yup.string().trim().required('Informe o código') : Yup.string(),
            password:
                step === 'reset'
                    ? Yup.string().trim().required('Informe a nova senha').min(6, 'A senha deve ter no mínimo 6 caracteres')
                    : Yup.string(),
            passwordConfirm:
                step === 'reset'
                    ? Yup.string()
                          .required('Confirme a nova senha')
                          .oneOf([Yup.ref('password')], 'As senhas não conferem')
                    : Yup.string(),
        }),
        onSubmit: async formValues => {
            try {
                const payload = {
                    username: formValues.username?.trim(),
                    profile,
                };

                if (step === 'request') {
                    await server.post('auth/lostpass', payload);
                    enqueueSnackbar('Enviamos um código para o e-mail cadastrado.', { variant: 'success' });
                    setStep('reset');
                    return;
                }

                await server.post('auth/lostpass', {
                    ...payload,
                    code: formValues.code?.trim(),
                    password: formValues.password,
                });
                enqueueSnackbar('Senha atualizada com sucesso.', { variant: 'success' });
                navigate(loginReturnTo, { replace: true });
            } catch (error) {
                setErrors(error);
            }
        },
    });

    function handleBackToRequest() {
        setStep('request');
        setFieldValue('code', '');
        setFieldValue('password', '');
        setFieldValue('passwordConfirm', '');
    }

    return (
        <Stack width="100vw" height="100vh" backgroundColor="#eee" alignItems="center" justifyContent="center">
            <Card sx={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
                <CardContent>
                    <Stack component="form" alignItems="center" justifyContent="center" onSubmit={handleSubmit}>
                        <Box width="100%" p={2}>
                            <Box component="img" src={Logo} alt="Logo" sx={{ width: 180, marginBottom: 3 }} />

                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                                {getStepTitle()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', mb: 2 }}>
                                {getStepDescription()}
                            </Typography>

                            <Stack gap={2}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    value={values.username}
                                    label={getFieldLabel()}
                                    onChange={handleChange}
                                    name="username"
                                    autoComplete="username"
                                    disabled={step === 'reset'}
                                    InputLabelProps={{ shrink: Boolean(values.username) }}
                                />

                                {step === 'reset' && (
                                    <>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            value={values.code}
                                            label="Código de recuperação"
                                            onChange={handleChange}
                                            name="code"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            InputLabelProps={{ shrink: Boolean(values.code) }}
                                        />
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            type="password"
                                            value={values.password}
                                            label="Nova senha"
                                            onChange={handleChange}
                                            name="password"
                                            autoComplete="new-password"
                                            InputLabelProps={{ shrink: Boolean(values.password) }}
                                        />
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            type="password"
                                            value={values.passwordConfirm}
                                            label="Confirmar nova senha"
                                            onChange={handleChange}
                                            name="passwordConfirm"
                                            autoComplete="new-password"
                                            InputLabelProps={{ shrink: Boolean(values.passwordConfirm) }}
                                        />
                                    </>
                                )}

                                <Button fullWidth type="submit" variant="contained" disabled={isSubmitting}>
                                    {isSubmitting
                                        ? 'Enviando...'
                                        : step === 'request'
                                          ? 'Enviar código'
                                          : 'Atualizar senha'}
                                </Button>

                                {step === 'reset' && (
                                    <Button fullWidth type="button" variant="outlined" onClick={handleBackToRequest}>
                                        Não recebi o código
                                    </Button>
                                )}

                                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                                    <Link to={loginReturnTo} style={{ fontSize: 14, color: 'inherit', textDecoration: 'none' }}>
                                        Voltar ao login
                                    </Link>
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );
}
